import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, errMsg } from "../api";
import { PaperclipIcon } from "./Icons";
import { Notice } from "./ui";

export type OwnerType = "transaction" | "transfer" | "good" | "loan";
export interface AttachmentInfo { id: string; fileName: string; contentType: string; sizeBytes: number; createdAt: string }

export const VIEWABLE_TYPES = new Set(["application/pdf", "image/png", "image/jpeg", "image/webp", "image/gif"]);
export const isViewable = (a: { contentType: string }) => VIEWABLE_TYPES.has(a.contentType);
export const viewUrl = (realm: string, id: string) => `/api/${realm}/attachments/${id}/view`;
export const downloadUrl = (realm: string, id: string) => `/api/${realm}/attachments/${id}/download`;
const SEGS: Record<OwnerType, string> = { transaction: "transactions", transfer: "transfers", good: "pat/goods", loan: "pat/loans" };
export const attachSeg = (t: OwnerType) => SEGS[t];

export const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 10;
export const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.xml,.ofx,.doc,.docx,.xls,.xlsx,.odt,.ods";
const OK_EXT = new Set(ACCEPT.split(",").map((e) => e.slice(1)));
const seg = (t: OwnerType) => SEGS[t];
const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Valida no navegador (o servidor valida de novo): extensão permitida e até 10 MB. */
export function checkFile(f: File): string | null {
  const ext = f.name.includes(".") ? f.name.split(".").pop()!.toLowerCase() : "";
  if (!OK_EXT.has(ext)) return `“${f.name}”: tipo não permitido (.${ext || "sem extensão"}).`;
  if (f.size < 1) return `“${f.name}” está vazio.`;
  if (f.size > MAX_BYTES) return `“${f.name}” excede 10 MB.`;
  return null;
}

export async function uploadAttachment(realm: string, type: OwnerType, id: string, file: File): Promise<AttachmentInfo> {
  const res = await fetch(`/api/${realm}/${seg(type)}/${id}/attachments?name=${encodeURIComponent(file.name)}`, {
    method: "POST", credentials: "same-origin", headers: { "content-type": "application/octet-stream", "x-mm-csrf": "1" }, body: file,
  });
  let data: any = null;
  try { data = await res.json(); } catch { /* sem corpo */ }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? "error", data?.message ?? (res.status === 413 ? "Arquivo grande demais (máximo de 10 MB)." : `Erro ${res.status}`));
  return data as AttachmentInfo;
}

/** Envia os arquivos escolhidos antes do registro existir; devolve o que falhou (o registro já está salvo). */
export async function uploadPending(realm: string, type: OwnerType, id: string, files: File[]): Promise<string[]> {
  const failed: string[] = [];
  for (const f of files) { try { await uploadAttachment(realm, type, id, f); } catch (e) { failed.push(`${f.name}: ${errMsg(e)}`); } }
  return failed;
}

/**
 * Campo "Anexos". Registro novo (`ownerId` ausente): guarda os arquivos em `pending` e quem salva envia depois (uploadPending).
 * Registro existente: envia na hora, lista, baixa e remove.
 */
export function AttachmentsField({ realm, ownerType, ownerId, pending, setPending, canWrite = true }: {
  realm: string; ownerType: OwnerType; ownerId?: string; pending: File[]; setPending: (f: File[]) => void; canWrite?: boolean;
}) {
  const [items, setItems] = useState<AttachmentInfo[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const load = useCallback(async () => {
    if (!ownerId) return;
    try { setItems(await api<AttachmentInfo[]>("GET", `/api/${realm}/${seg(ownerType)}/${ownerId}/attachments`)); } catch (e) { setErr(errMsg(e)); }
  }, [realm, ownerType, ownerId]);
  useEffect(() => { void load(); }, [load]);

  async function pick(list: FileList | null) {
    if (!list || !list.length) return;
    setErr(null);
    const files = [...list];
    const bad = files.map(checkFile).find(Boolean);
    if (bad) { setErr(bad); if (input.current) input.current.value = ""; return; }
    if (items.length + pending.length + files.length > MAX_FILES) { setErr(`Limite de ${MAX_FILES} anexos por registro.`); if (input.current) input.current.value = ""; return; }
    if (!ownerId) setPending([...pending, ...files]);
    else {
      setBusy(true);
      const failed = await uploadPending(realm, ownerType, ownerId, files);
      if (failed.length) setErr(failed.join(" "));
      await load(); setBusy(false);
    }
    if (input.current) input.current.value = "";
  }
  async function remove(a: AttachmentInfo) {
    setErr(null);
    try {
      const res = await fetch(`/api/${realm}/attachments/${a.id}`, { method: "DELETE", credentials: "same-origin", headers: { "x-mm-csrf": "1" } });
      if (!res.ok) throw new ApiError(res.status, "error", "Não foi possível remover o anexo.");
      await load();
    } catch (e) { setErr(errMsg(e)); }
  }

  return (
    <div className="attach" data-attachments>
      <div className="attach-head"><span><PaperclipIcon /> Anexos <span className="muted small">(boletos, contratos, comprovantes · até 10 MB cada)</span></span>
        {canWrite && <button type="button" className="btn ghost small-btn" onClick={() => input.current?.click()} disabled={busy} data-attach-add>{busy ? "Enviando…" : "+ Anexar arquivo"}</button>}
        <input ref={input} type="file" multiple accept={ACCEPT} hidden onChange={(e) => void pick(e.target.files)} data-attach-input />
      </div>
      {err && <Notice kind="error">{err}</Notice>}
      {(items.length > 0 || pending.length > 0) && (
        <ul className="attach-list">
          {items.map((a) => (
            <li key={a.id}>{isViewable(a)
                ? <a href={viewUrl(realm, a.id)} target="_blank" rel="noopener noreferrer" title="Abrir para visualizar (sem baixar)" data-attach-view>{a.fileName}</a>
                : <a href={downloadUrl(realm, a.id)} target="_blank" rel="noopener noreferrer" title="Este tipo de arquivo só pode ser baixado">{a.fileName}</a>}<span className="muted small">{kb(a.sizeBytes)}{isViewable(a) ? "" : " · baixar"}</span>
              {canWrite && <button type="button" className="link danger" onClick={() => void remove(a)} data-attach-remove>Remover</button>}</li>
          ))}
          {pending.map((f, i) => (
            <li key={`p${i}`}><span>{f.name}</span><span className="muted small">{kb(f.size)} · será anexado ao salvar</span>
              <button type="button" className="link danger" onClick={() => setPending(pending.filter((_, j) => j !== i))}>Tirar</button></li>
          ))}
        </ul>
      )}
    </div>
  );
}
